import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Landmark, Home as HomeIcon, Compass } from 'lucide-react';
import { MarketingHeader } from '@/components/layout/MarketingHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { TopoBackground } from '@/components/layout/TopoBackground';
import { ConsultantCallout } from '@/components/ConsultantCallout';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { track } from '@/features/analytics';
import { countriesQuery, type Country } from '@/data';
import {
  ContourPatch,
  DimensionMicroPreview,
  DimensionsGrid,
  FaqAccordion,
  LiveRankingPreview,
  MapTeaser,
  TickDivider,
  TrustStrip,
  buildFaqJsonLd,
} from '@/features/marketing';
import { buildWikiUrl } from '@/features/wiki/architecture';
import { isPublishedCountry } from '@/features/wiki/published-countries';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { uiLang } from '@/i18n/lang';

/**
 * Marketing landing — "Quiet Atlas" direction. Nine sections, single
 * SEO-friendly h1 in hero, JSON-LD FAQPage emitted for rich snippets.
 *
 * Routing: `/` mounts this page. Quiz / countries / Italy map are reached
 * via primary + secondary CTAs only.
 */
export default function HomePage() {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useSeoMeta({
    title: t('home.meta.title'),
    description: t('home.meta.description'),
    ogImage:
      typeof window !== 'undefined'
        ? `${window.location.origin}/og-image.png`
        : undefined,
    locale: uiLang(i18n.language) === 'ru' ? 'ru_RU' : 'en_US',
  });

  useJsonLd(buildFaqJsonLd(t), 'home-faq');

  // Scroll to in-page anchor when navigating from header nav (`/#methodology`).
  // React Router doesn't do this automatically for SPA Links — it only updates
  // the URL hash. Without this useEffect, clicking «Methodology» in the header
  // changes the URL but the page stays at the top.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    // Wait one frame so sections (most of which are below the fold) have
    // mounted before we measure their position.
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);

  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink-700">
      <MarketingHeader />

      <main id="main-content" className="flex-1">
        <Hero />
        <TrustStrip />
        <DimensionMicroPreview />
        <HowItWorks />
        <PillarsSection />
        <TickDivider left="45°N" right="11°E" />
        <DimensionsGrid />
        <AtlasTeaser />
        <MapTeaser />
        <MethodNote />
        <FaqAccordion />
        <FooterCta />
      </main>

      <StickyCtaMobile />
      <SiteFooter />
    </div>
  );
}

// Mobile-only sticky CTA. Shows only after the hero primary CTA scrolls
// out of view — duplicating the hero button while it's still on-screen
// felt redundant and the docked button stole visual weight from the hero.
// IntersectionObserver on the hero CTA is cheaper than a scroll listener
// and avoids re-layout per scroll tick.
//
// Padding floor: pb-safe alone collapsed to 0 on browsers without
// env(safe-area-inset-bottom) — use max(…) so we always keep ≥0.75rem of
// breathing room above the tab bar / home indicator.
function StickyCtaMobile() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target = document.querySelector('[data-testid="hero-primary-cta"]');
    if (!target) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setVisible(!entry.isIntersecting);
      },
      { rootMargin: '-20% 0px 0px 0px' },
    );
    io.observe(target);
    return () => io.disconnect();
  }, []);

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-3 bottom-3 z-[1300] transition-opacity duration-200 sm:hidden ${
        visible ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      style={{ paddingBottom: 'max(0px, env(safe-area-inset-bottom))' }}
    >
      <Link
        to="/quiz"
        tabIndex={visible ? 0 : -1}
        className="flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-terra-600 px-5 py-3.5 text-base font-semibold text-paper shadow-2xl transition-colors hover:bg-terra-700"
        data-testid="sticky-cta-mobile"
      >
        {t('home.hero.primaryCta')}
        <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function Hero() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  return (
    <section
      aria-labelledby="hero-title"
      className="relative overflow-hidden bg-paper"
    >
      <TopoBackground className="absolute inset-0" opacity={0.06} />

      {/* Atlas ornaments — country-agnostic decorations only. Single-country
          silhouettes intentionally avoided in the hero so the brand reads as
          "multi-country atlas" rather than "Italy product".
          NOTE: the decorative <CompassRose> in the top-left was removed
          because the new Lodestar mark in the MarketingHeader sits in
          almost the same position — having both read as a duplicate /
          mis-aligned brand glyph. The contour patches still anchor the
          composition without competing with the mark. */}
      <ContourPatch
        className="absolute -bottom-10 -left-16 w-[520px]"
        opacity={0.16}
      />
      <ContourPatch
        className="absolute -right-24 -top-8 w-[380px] rotate-[160deg]"
        opacity={0.1}
      />

      <div className="relative z-[2] mx-auto grid max-w-6xl gap-12 px-6 py-16 sm:py-24 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-6 lg:pr-6">
          <h1
            id="hero-title"
            className="font-display text-[32px] font-normal leading-[1.08] tracking-tight text-ink-900 break-words sm:text-[44px] lg:text-[56px]"
          >
            {t('home.hero.title')}
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink-500">
            {t('home.hero.sub')}
          </p>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink-500">
            {t('home.hero.tagline')}
          </p>
          <p className="mt-3 inline-flex max-w-lg items-center gap-1.5 rounded-md bg-paper-2 px-2.5 py-1 font-mono text-[11px] tracking-wider text-ink-500">
            <span aria-hidden="true">🇮🇹</span>
            {t('home.hero.italyOnly', {
              defaultValue: 'Detailed comune-level map: Italy only · Quiz: all 31 countries',
            })}
          </p>

          <div className="mt-10 flex flex-col items-start gap-4">
            <Link
              to="/quiz"
              className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-md bg-terra-600 px-6 py-3 font-sans text-base font-medium text-paper shadow-sm transition-colors hover:bg-terra-700 focus:outline-none focus:ring-2 focus:ring-terra-600/30 focus:ring-offset-2 focus:ring-offset-paper"
              data-testid="hero-primary-cta"
            >
              {t('home.hero.primaryCta')}
              <span aria-hidden="true" className="font-mono text-sm opacity-80">
                →
              </span>
            </Link>
            <Link
              to={lp('/countries')}
              className="font-mono text-xs font-medium uppercase tracking-wider text-ink-500 hover:text-ink-900"
              data-testid="hero-secondary-cta"
            >
              {t('home.hero.secondaryCta')} →
            </Link>
          </div>
        </div>

        <div className="relative lg:col-span-6">
          <LiveRankingPreview />
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const steps = ['s1', 's2', 's3'] as const;
  return (
    <section
      aria-labelledby="how-title"
      className="border-b border-ink-100 bg-paper"
    >
      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        <h2
          id="how-title"
          className="font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl"
        >
          {t('home.how.title')}
        </h2>

        <ol className="mt-12 grid gap-10 sm:grid-cols-3 sm:gap-8">
          {steps.map((s) => (
            <li key={s}>
              <p
                aria-hidden="true"
                className="font-mono text-[40px] font-medium leading-none text-ink-300"
              >
                {t(`home.how.${s}.n`)}
              </p>
              <h3 className="mt-3 font-display text-2xl font-medium italic tracking-tight text-ink-900">
                {t(`home.how.${s}.title`)}
              </h3>
              <p className="mt-3 max-w-xs text-base leading-relaxed text-ink-500">
                {t(`home.how.${s}.body`)}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-12">
          <Link
            to="/quiz"
            className="inline-flex items-center gap-2 rounded-md bg-terra-600 px-5 py-2.5 font-sans text-sm font-medium text-paper transition-colors hover:bg-terra-700"
          >
            {t('home.hero.primaryCta')}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

// CV.7 — relocation-pillars section. Four equally-weighted "why people
// relocate" cards (tax, career, family, independence). Career-pillar links
// to the M0 holding page `/career`; the other three link to flagship
// content within published countries. SEO-positive (topical breadth) and
// product-positive (signals career as a first-class vector without forking
// the funnel). See docs/roadmap/career-vector/CV.7-landing-career-pillar.md.
function PillarsSection() {
  const { t } = useTranslation();
  const pillars = [
    { id: 'tax', icon: Landmark, href: '/countries/portugal/taxes' },
    { id: 'career', icon: Briefcase, href: '/career' },
    { id: 'family', icon: HomeIcon, href: '/countries/italy' },
    { id: 'independence', icon: Compass, href: '/countries/uae' },
  ] as const;

  return (
    <section
      aria-labelledby="pillars-title"
      className="border-b border-ink-100 bg-paper-2"
    >
      <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <h2
          id="pillars-title"
          className="font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl"
        >
          {t('home.pillars.title')}
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-500">
          {t('home.pillars.subtitle')}
        </p>

        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {pillars.map((p) => {
            const Icon = p.icon;
            const isCareer = p.id === 'career';
            return (
              <article
                key={p.id}
                aria-labelledby={`pillar-${p.id}-title`}
                className="flex flex-col"
              >
                <Icon
                  aria-hidden="true"
                  className="h-7 w-7 text-terra-600"
                  strokeWidth={1.5}
                />
                <h3
                  id={`pillar-${p.id}-title`}
                  className="mt-4 font-display text-xl font-medium italic tracking-tight text-ink-900"
                >
                  {t(`home.pillars.${p.id}.title`)}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-500">
                  {t(`home.pillars.${p.id}.body`)}
                </p>
                <Link
                  to={p.href}
                  onClick={() => {
                    if (isCareer) {
                      track('career_pillar_click', { source: 'landing_pillars' });
                    }
                  }}
                  className="mt-4 inline-flex items-center gap-1 font-mono text-xs font-medium uppercase tracking-wider text-terra-600 hover:text-terra-700"
                  data-testid={isCareer ? 'pillar-career-cta' : undefined}
                >
                  {t(`home.pillars.${p.id}.link`)}
                  <span aria-hidden="true">→</span>
                </Link>
              </article>
            );
          })}
        </div>

        <p className="mt-10 max-w-2xl text-sm leading-relaxed text-ink-500">
          {t('home.pillars.bridge')}
        </p>
      </div>
    </section>
  );
}

function AtlasTeaser() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const lp = useLocalizedPath();
  const { data: file } = useQuery(countriesQuery());

  // Honest preview: published guides first (clickable), then the next tier
  // for visual depth. Floors at 6 rows total.
  const preview = useMemo<Country[]>(() => {
    if (!file) return [];
    const tierRank: Record<string, number> = { T0: 0, T1: 1, T2: 2, T3: 3 };
    const byTier = [...file.countries].sort(
      (a, b) => (tierRank[a.tier] ?? 9) - (tierRank[b.tier] ?? 9),
    );
    const published = byTier.filter((c) => isPublishedCountry(c.code));
    const rest = byTier.filter((c) => !isPublishedCountry(c.code));
    return [...published, ...rest].slice(0, 6);
  }, [file]);

  return (
    <section
      aria-labelledby="atlas-title"
      className="bg-paper-2/40"
    >
      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        <h2
          id="atlas-title"
          className="font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl"
        >
          {t('home.atlas.title')}
        </h2>

        <ul className="mt-8 space-y-3 text-base leading-relaxed text-ink-700">
          <li>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-terra-600">
              T0
            </span>{' '}
            {t('home.atlas.tierT0').replace(/^T0\s—\s/u, '')}
          </li>
          <li>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-moss-600">
              T1
            </span>{' '}
            {t('home.atlas.tierT1').replace(/^T1\s—\s/u, '')}
          </li>
          <li>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-500">
              T2/T3
            </span>{' '}
            {t('home.atlas.tierT2T3').replace(/^T2 \/ T3\s—\s/u, '')}
          </li>
        </ul>

        <div className="mt-10 rounded-lg border border-ink-100 bg-paper px-5 py-4 sm:px-8 sm:py-6">
          <ul className="divide-y divide-ink-100">
            {preview.map((c) => {
              const name = lang === 'ru' ? c.name_ru : c.name_en;
              const published = isPublishedCountry(c.code);
              const hubHref = buildWikiUrl({
                lang: lang === 'ru' ? 'ru' : 'en',
                cc: c.code,
              });
              const rowInner = (
                <>
                  <span className="text-2xl leading-none" aria-hidden="true">
                    {c.flag_emoji}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-display text-lg font-medium italic text-ink-900">
                      {name}
                    </span>
                    <span className="ml-3 font-mono text-[11px] uppercase tracking-wider text-ink-500">
                      {c.code} · {c.region}
                    </span>
                  </span>
                  {published ? (
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-terra-600">
                      {t('home.atlas.guideBadge', { defaultValue: 'Guide →' })}
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
                      {c.tier}
                    </span>
                  )}
                </>
              );
              return (
                <li
                  key={c.code}
                  className="py-3 first:pt-0 last:pb-0"
                >
                  {published ? (
                    <Link
                      to={hubHref}
                      className="flex items-center gap-3 -mx-2 px-2 py-1 rounded-md hover:bg-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600"
                      data-testid={`atlas-row-${c.code.toLowerCase()}`}
                    >
                      {rowInner}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3">
                      {rowInner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link
              to={lp('/countries')}
              className="inline-flex font-mono text-xs font-medium uppercase tracking-wider text-terra-600 hover:underline"
              data-testid="atlas-browse-all"
            >
              {t('home.atlas.browseAll')}
            </Link>
            <Link
              to={lp('/guide')}
              className="inline-flex font-mono text-xs font-medium uppercase tracking-wider text-ink-500 hover:text-ink-900"
              data-testid="atlas-guide-cta"
            >
              {t('home.atlas.guideCta', { defaultValue: 'How to use the map →' })}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function MethodNote() {
  const { t } = useTranslation();
  return (
    <section
      id="methodology"
      aria-labelledby="method-title"
      className="bg-paper"
    >
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        <h2
          id="method-title"
          className="font-display text-2xl font-medium italic tracking-tight text-ink-900 sm:text-3xl"
        >
          {t('home.method.title')}
        </h2>

        <div className="mt-6 space-y-4 text-base leading-relaxed text-ink-700">
          <p>{t('home.method.body1')}</p>
          <p>{t('home.method.body2')}</p>
          <p className="font-display text-lg italic text-ink-900">
            {t('home.method.body3')}
          </p>
        </div>

        {/* Property-tax knowledge-base note — surfaces the second tax queue
            the catalogue / AI helper now use, with sources for credibility. */}
        <div className="mt-10 rounded-lg border border-ink-100 bg-paper-2 px-5 py-4 text-sm leading-relaxed text-ink-700">
          <p className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
            {t('home.method.propertyTaxLabel', { defaultValue: 'New: property-tax dataset' })}
          </p>
          <p className="mt-2">
            {t('home.method.propertyTaxBody', {
              defaultValue:
                'Beyond income tax, every country card now carries a verified property-tax tier (annual ownership tax + purchase transaction + capital gains + wealth tax). Sourced from OECD, EU Taxes in Europe Database, Global Property Guide and KPMG. Half-life is ~12 months — tax codes change at fiscal year start, so we re-verify quarterly.',
            })}
          </p>
        </div>
      </div>
    </section>
  );
}

function FooterCta() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  return (
    <section
      aria-labelledby="footer-cta-title"
      className="relative overflow-hidden border-y border-ink-100 bg-paper-2/60"
    >
      <TopoBackground className="absolute inset-0" opacity={0.05} />
      <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
        <h2
          id="footer-cta-title"
          className="font-display text-[36px] font-medium leading-[1.05] tracking-tight text-ink-900 sm:text-[48px]"
        >
          {t('home.footer.ctaTitle1')}
          <br />
          {t('home.footer.ctaTitle2')}
        </h2>

        <div className="mt-10 flex flex-col items-center gap-4">
          <Link
            to="/quiz"
            className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-md bg-terra-600 px-6 py-3 font-sans text-base font-medium text-paper shadow-sm transition-colors hover:bg-terra-700"
            data-testid="footer-primary-cta"
          >
            {t('home.footer.ctaPrimary')}
            <span aria-hidden="true">→</span>
          </Link>
          <Link
            to={lp('/countries')}
            className="font-mono text-xs font-medium uppercase tracking-wider text-ink-500 hover:text-ink-900"
          >
            {t('home.footer.ctaSecondary')}
          </Link>
        </div>

        {/* Quiet escape hatch for users who want a human before doing the
            quiz. Sits below the main CTA so it doesn't compete for
            attention but is reachable without scrolling further. */}
        <div className="mx-auto mt-12 max-w-xl text-left">
          <ConsultantCallout source="home" />
        </div>
      </div>
    </section>
  );
}

